import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

export type CopilotStatus =
  | 'success'
  | 'partial'
  | 'clarification_required'
  | 'access_denied'
  | 'validation_error'
  | 'query_limit_exceeded'
  | 'service_error';

export interface CopilotChatResponse {
  success: boolean;
  conversationId: string | null;
  messageId?: string;
  answer: string;
  status: CopilotStatus;
  metadata?: {
    entitiesUsed?: string[];
    partialResult?: boolean;
    omittedFields?: string[];
    executionTimeMs?: number;
  };
}

export interface CopilotConversationSummary {
  id: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface CopilotConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  status: string;
  safe_metadata?: Record<string, unknown>;
  created_at: string;
}

export interface CopilotAdminPermissionAssignment {
  code: string;
  effect: 'ALLOW' | 'DENY';
  enabled?: boolean;
}

export interface KyaraCatalogField {
  key: string;
  description: string;
  type: string;
  sensitive?: boolean;
  selectable?: boolean;
  filterable?: boolean;
  sortable?: boolean;
  groupable?: boolean;
  aggregatable?: boolean;
  enabled?: boolean;
  granted?: boolean;
}

export interface KyaraCatalogEntity {
  key: string;
  module: string;
  description: string;
  modelName?: string;
  tableName?: string | null;
  discovered?: boolean;
  permissionCode?: string;
  enabled?: boolean;
  granted?: boolean;
  allFields?: boolean;
  fields: KyaraCatalogField[];
  rowAccess?: {
    mode: 'NONE' | 'ALL' | 'SCOPED';
    fieldKey?: string;
    operator?: 'eq' | 'in';
    scopeType?: string;
    sourceKey?: string | null;
    staticValue?: unknown;
  };
}

export interface CopilotAdminCatalogResponse {
  entities: KyaraCatalogEntity[];
  relations: Array<{
    key: string;
    sourceEntity: string;
    targetEntity: string;
    type: string;
    enabled: boolean;
  }>;
  capabilities: Record<string, unknown>;
}

export interface KyaraUserSearchResult {
  id: number;
  external_id?: string;
  nombre: string;
  apellidos?: string;
  correo: string;
  username?: string;
  rol?: string;
  activo: boolean;
}

@Injectable({ providedIn: 'root' })
export class CopilotService {
  private readonly apiUrl = `${environment.apiUrl}/copilot`;

  constructor(private http: HttpClient) {}

  status(): Observable<{
    success: boolean;
    name?: string;
    enabled: boolean;
    authenticated: boolean;
    hasAccess?: boolean;
    accessMessage?: string | null;
    canAdmin?: boolean;
    maxMessageLength?: number;
  }> {
    return this.http.get<{
      success: boolean;
      name?: string;
      enabled: boolean;
      authenticated: boolean;
      hasAccess?: boolean;
      accessMessage?: string | null;
      canAdmin?: boolean;
      maxMessageLength?: number;
    }>(`${this.apiUrl}/status`);
  }

  chat(message: string, conversationId: string | null): Observable<CopilotChatResponse> {
    return this.http.post<CopilotChatResponse>(`${this.apiUrl}/chat`, {
      conversationId,
      message,
    });
  }

  listConversations(): Observable<{
    success: boolean;
    data: CopilotConversationSummary[];
  }> {
    return this.http.get<{
      success: boolean;
      data: CopilotConversationSummary[];
    }>(`${this.apiUrl}/conversations`);
  }

  getConversation(id: string): Observable<{
    success: boolean;
    data: CopilotConversationSummary & { messages: CopilotConversationMessage[] };
  }> {
    return this.http.get<{
      success: boolean;
      data: CopilotConversationSummary & { messages: CopilotConversationMessage[] };
    }>(`${this.apiUrl}/conversations/${id}`);
  }

  getAdminCatalog(): Observable<{ success: boolean; data: CopilotAdminCatalogResponse }> {
    return this.http.get<{ success: boolean; data: CopilotAdminCatalogResponse }>(
      `${this.apiUrl}/admin/catalog`,
    );
  }

  syncCatalog(): Observable<{
    success: boolean;
    message: string;
    data: {
      entities: number;
      fields: number;
      relations: number;
      permissions: number;
      totalEntities: number;
      totalFields: number;
    };
  }> {
    return this.http.post<any>(`${this.apiUrl}/admin/catalog/sync`, {});
  }

  updateCatalogEntity(entityKey: string, enabled: boolean): Observable<{ success: boolean; data: any }> {
    return this.http.put<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/catalog/entities/${encodeURIComponent(entityKey)}`,
      { enabled },
    );
  }

  updateCatalogField(
    entityKey: string,
    fieldKey: string,
    enabled: boolean,
  ): Observable<{ success: boolean; data: any }> {
    return this.http.put<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/catalog/entities/${encodeURIComponent(entityKey)}/fields/${encodeURIComponent(fieldKey)}`,
      { enabled },
    );
  }

  updateCatalogRelation(relationKey: string, enabled: boolean): Observable<{ success: boolean; data: any }> {
    return this.http.put<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/catalog/relations/${encodeURIComponent(relationKey)}`,
      { enabled },
    );
  }

  searchUsers(query: string): Observable<{ success: boolean; data: KyaraUserSearchResult[] }> {
    return this.http.get<{ success: boolean; data: KyaraUserSearchResult[] }>(
      `${this.apiUrl}/admin/users/search`,
      { params: { q: query, limit: 30 } },
    );
  }

  getUserCatalogAccess(userId: string | number): Observable<{
    success: boolean;
    data: {
      userId: string;
      defaultPolicy: 'DENY';
      readOnly: true;
      entities: KyaraCatalogEntity[];
    };
  }> {
    return this.http.get<any>(
      `${this.apiUrl}/admin/users/${encodeURIComponent(String(userId))}/catalog-access`,
    );
  }

  replaceUserCatalogAccess(
    userId: string | number,
    entities: Array<{
      entityKey: string;
      granted: boolean;
      allFields: boolean;
      fields: string[];
      rowAccess: Record<string, unknown>;
    }>,
  ): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.apiUrl}/admin/users/${encodeURIComponent(String(userId))}/catalog-access`,
      { entities },
    );
  }

  getPromptHelp(): Observable<{ success: boolean; data: any }> {
    return this.http.get<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/prompts/help`,
    );
  }

  getAdminPermissions(): Observable<{ success: boolean; data: any }> {
    return this.http.get<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/permissions`,
    );
  }

  replaceRolePermissions(
    roleKey: string,
    permissions: CopilotAdminPermissionAssignment[],
  ): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.apiUrl}/admin/roles/${encodeURIComponent(roleKey)}/permissions`,
      { permissions },
    );
  }

  replaceUserPermissions(
    userId: string,
    permissions: CopilotAdminPermissionAssignment[],
  ): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.apiUrl}/admin/users/${encodeURIComponent(userId)}/permissions`,
      { permissions },
    );
  }

  listFieldPolicies(): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(
      `${this.apiUrl}/admin/field-policies`,
    );
  }

  saveFieldPolicy(payload: Record<string, unknown>): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/field-policies`,
      payload,
    );
  }

  listRowScopes(): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(
      `${this.apiUrl}/admin/row-scopes`,
    );
  }

  saveRowScope(payload: Record<string, unknown>): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/row-scopes`,
      payload,
    );
  }

  listPrompts(): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(
      `${this.apiUrl}/admin/prompts`,
    );
  }

  createPromptVersion(payload: Record<string, unknown>): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/prompts`,
      payload,
    );
  }

  getAdminSettings(): Observable<{ success: boolean; data: any }> {
    return this.http.get<{ success: boolean; data: any }>(
      `${this.apiUrl}/admin/settings`,
    );
  }

  updateAdminSettings(payload: Record<string, unknown>): Observable<{
    success: boolean;
    data: any;
    message: string;
  }> {
    return this.http.put<{ success: boolean; data: any; message: string }>(
      `${this.apiUrl}/admin/settings`,
      payload,
    );
  }

  getAudit(limit = 100): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(
      `${this.apiUrl}/admin/audit`,
      { params: { limit } },
    );
  }
}
